/**
 * WellnessTab — Careflow plan + Rounds display for the area.
 *
 * Filters:
 *   Careflow windows:
 *     - escalation_type_id 5 (area group, matching this area's groups)
 *     - escalation_type_id 1 (default / campus-wide)
 *
 *   Rounds windows:
 *     - area_group_id_list overlaps with this area's groups
 *
 * Display order: Area-based → Default
 */

import { useMemo } from 'react';
import { convertSectoHHMM, HHMMtoLocalTime } from '../../../../../libs.jsx';

const WellnessTab = ({ careData, roundsData, areaGroups }) => {
  // Build careflow items filtered by area groups + default
  const careflowItems = useMemo(() => {
    if (!careData?.length) return [];

    const areaFlows = [];
    const defaultFlows = [];
    const groupIds = new Set((areaGroups || []).map((g) => g.area_group_id?.toString()));

    for (const cf of careData) {
      const item = {
        name: cf.careflow_description,
        window: `${HHMMtoLocalTime(convertSectoHHMM(cf.enable_start))} - ${HHMMtoLocalTime(convertSectoHHMM(cf.enable_end))}`,
      };

      if (cf.escalation_type_id === 1) {
        defaultFlows.push({ ...item, type: 'Default' });
      } else if (cf.escalation_type_id === 5) {
        // Area group: check if any of this area's groups match
        const refs = cf.references?.split(',') || [];
        for (const ref of refs) {
          if (groupIds.has(ref)) {
            areaFlows.push({ ...item, type: 'Area' });
            break;
          }
        }
      }
    }

    return [...areaFlows, ...defaultFlows];
  }, [careData, areaGroups]);

  // Build rounds items filtered by area groups
  const roundsItems = useMemo(() => {
    if (!roundsData?.length) return [];

    const items = [];
    const groupIds = new Set((areaGroups || []).map((g) => g.area_group_id?.toString()));

    for (const rd of roundsData) {
      const refs = rd.area_group_id_list?.split(',') || [];
      for (const ref of refs) {
        if (groupIds.has(ref)) {
          items.push({
            name: rd.description,
            window: `${HHMMtoLocalTime(convertSectoHHMM(rd.window_start))} - ${HHMMtoLocalTime(convertSectoHHMM(rd.window_end))}`,
          });
          break;
        }
      }
    }

    return items;
  }, [roundsData, areaGroups]);

  return (
    <div className="pp-wellness-grid">
      {/* Careflow Plan */}
      <div className="pp-section">
        <div className="pp-section__header">
          <h3 className="pp-section__title">Careflow Plan</h3>
        </div>
        <div className="pp-section__body">
          {careflowItems.length > 0 ? (
            <div>
              {careflowItems.map((item, idx) => (
                <div key={idx} className="ip-careflow-item">
                  <span className="ip-careflow-item__name" title={item.name}>
                    {item.name}
                  </span>
                  <span className="ip-careflow-item__window">{item.window}</span>
                  <span className="ip-careflow-item__type">{item.type}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="dp-empty">
              <p className="dp-empty__title">No Careflow</p>
              <p className="dp-empty__text">No careflow plans are assigned to this area's groups.</p>
            </div>
          )}
        </div>
      </div>

      {/* Rounds */}
      <div className="pp-section">
        <div className="pp-section__header">
          <h3 className="pp-section__title">Rounds</h3>
        </div>
        <div className="pp-section__body">
          {roundsItems.length > 0 ? (
            <div>
              {roundsItems.map((item, idx) => (
                <div key={idx} className="ip-careflow-item">
                  <span className="ip-careflow-item__name" title={item.name}>
                    {item.name}
                  </span>
                  <span className="ip-careflow-item__window">{item.window}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="dp-empty">
              <p className="dp-empty__title">No Rounds Needed</p>
              <p className="dp-empty__text">No rounds schedules are assigned to this area's groups.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WellnessTab;
